import type { IntlShape } from "react-intl";
import {
  buildCandidateRanking,
  buildPowerFit,
  buildRadiusInsight,
  buildRankingInsights,
  conditionTypename,
  constraintSummary,
  isLenientLevelMatch,
  type RankSortBy,
  type RankThresholds,
  type RankedCandidate,
  type RankedCriterion,
  type RankingCriterionInsight,
  type RequirementLeniencyView,
} from "@/components/match/match-result-derive";
import type { FactSkillMemberView, TeamResultView } from "@/components/match/match-result-codec";
import { resolveTeamFit, type TeamFitBasisLike } from "@/components/match/match-result-team-fit";
import {
  buildCompanyRankingInsights,
  companyBand,
  type CompanyCriterionView,
  type RankedCompany,
} from "@/components/match/company-ranking-derive";
import type {
  MatchResultPdfRank,
  MatchResultPdfRankCandidate,
  MatchResultPdfRankCriterionRow,
  MatchResultPdfRankCriterionState,
  MatchResultPdfRankMessageRef,
  MatchResultPdfRequirementMatrix,
} from "./match-result-pdf-model";

// Wie viele Kriterien je Spalte die Match-Analyse zeigt. Derselbe Deckel wie auf dem Schirm,
// wo "Top 3" im Titel steht.
const RANK_INSIGHT_ROW_CAP = 3;

/**
 * Alles, was die Ableitung aus dem Host braucht und im Renderer-Bundle nicht existiert:
 * Labels und Ist-Werte aufloesen Referenzdaten aus Apollo (Abteilungen, Laender, Branchen),
 * der Renderer hat nur einen IntlProvider (@adr SPARK-0044-match-result-pdfs-use-a-dedicated-spa-renderer@3).
 */
export interface RankPdfFormatters {
  /** Kriteriums-Label inklusive aufgeloester Referenzdaten. */
  label: (condition: unknown) => string;
  /** Ist-Wert einer Person zu diesem Kriterium, referenzdaten-aufgeloest. */
  actual: (condition: unknown, actual: string | null) => string | null;
  kindOrder: (condition: unknown) => number;
  intl: IntlShape;
}

export interface RankPdfInput {
  view: TeamResultView;
  persons: Array<{ id: string; name: string; profession?: string | null }>;
  thresholds: RankThresholds;
  sortBy: RankSortBy;
  elementWeights?: Record<string, number> | null;
  requirementLeniency: RequirementLeniencyView;
  /** personDetails-Abschnitt an: nur dann reist der schwere Detailblock mit. */
  withDetails: boolean;
  /** matrices-Abschnitt an. */
  withMatrix: boolean;
  /**
   * Wer im Dokument steht. null = noch keine Wahl getroffen, dann greift die Vorgabe:
   * die Baender A und B, und wenn die leer waeren, alle. Personen-Ids statt einer Regel, weil
   * der Schwellenregler und die Sortierachse die Baender jederzeit neu schneiden - "bis B" als
   * gespeicherte Regel hiesse morgen etwas anderes als heute.
   */
  selectedPersonIds: ReadonlySet<string> | null;
  /**
   * Das im Design gemerkte Bestandsteam, als Rueckfall fuer den Ausschluss der Mitglieder aus der
   * Kandidatenliste. Der Ergebnis-Blob verliert `recruitingTeamFit` komplett, sobald ein Mitglied
   * die Einwilligung zu seinen Power-Werten zurueckzieht — ohne diesen Rueckfall taucht das
   * Bestandsteam im Export wieder als Kandidat auf, waehrend der Bildschirm es weiter ausschliesst
   * (ResultStep uebergibt denselben Rueckfall an das Panel).
   */
  teamFitBasis?: TeamFitBasisLike | null;
  formatters: RankPdfFormatters;
}

/**
 * Qualitative Einordnung der KI-Eignung. Dieselben Schwellen wie SuitabilityBand auf dem Schirm
 * (90/75/60/40); die Grenzen gehoeren zur genesis-Kalibrierung, nicht zur Darstellung, deshalb
 * liegen sie hier und nicht im Renderer.
 */
export function suitabilityRatingId(percent: number): string {
  if (percent >= 90) return "match.result.ranking.assessment.ratingVeryHigh";
  if (percent >= 75) return "match.result.ranking.assessment.ratingHigh";
  if (percent >= 60) return "match.result.ranking.assessment.ratingMedium";
  if (percent >= 40) return "match.result.ranking.assessment.ratingLow";
  return "match.result.ranking.assessment.ratingVeryLow";
}

/**
 * Freitext der KI-Bloecke in Punkte zerlegen - dieselbe Erkennung wie auf dem Schirm. Kein
 * Aufzaehlungszeichen im Text: ein einzelner Punkt, damit der Renderer nur einen Fall kennt.
 */
export function assessmentItems(text: string | null | undefined): string[] {
  const trimmed = (text ?? "").trim();
  if (!trimmed) return [];
  if (/^\s*(?:•|[-*])\s+/.test(trimmed) || /^•/.test(trimmed)) {
    if (/^•/.test(trimmed)) {
      return trimmed.split(/\s*•\s*/).map((item) => item.trim()).filter(Boolean);
    }
    return trimmed
      .split(/\r?\n/)
      .map((line) => line.replace(/^\s*[-*]\s+/, "").trim())
      .filter(Boolean);
  }
  return [trimmed];
}

/**
 * Pruefgrund einer REVIEW-Zeile. Auf dem Schirm sind das drei verschiedene Saetze, im PDF war es
 * bisher das Wort "Zu pruefen" - die Unterscheidung ist aber genau die Information, die ein
 * Leser braucht, um zu wissen, wo er nachsehen muss.
 */
function reviewReasonId(
  band: string,
  qualification: string,
  score: number | null,
): string | null {
  if (band !== "REVIEW") return null;
  if (qualification === "REVIEW_REQUIRED" && score === null) {
    return "match.result.ranking.reviewReasonBoth";
  }
  if (qualification === "REVIEW_REQUIRED") return "match.result.ranking.reviewReasonMust";
  return "match.result.ranking.reviewReasonKann";
}

function percent(value: number | null | undefined): number | null {
  return value == null ? null : Math.round(value * 100);
}

function criterionRow(
  insight: RankingCriterionInsight,
  formatters: RankPdfFormatters,
  withGap: boolean,
): MatchResultPdfRankCriterionRow {
  return {
    label: formatters.label(insight.condition),
    isMust: insight.requirement === "MUST",
    met: insight.metCount,
    total: insight.total,
    unknown: insight.unknownCount,
    aiMet: insight.aiMetCount,
    share: insight.total > 0 ? insight.metCount / insight.total : 0,
    gap: withGap ? Math.max(0, 1 - insight.averageFulfillment) : null,
    distribution: distributionText(insight, formatters),
  };
}

/**
 * Die Verteilung als Text statt als Achse. Der Bildschirm zeichnet eine SD-Spanne mit Median-,
 * Mittel- und Soll-Marken; auf Papier traegt dieselbe Aussage die Legende allein, und die vier
 * Zahlen sind ohnehin das, was zitiert wird.
 */
function distributionText(
  insight: RankingCriterionInsight,
  formatters: RankPdfFormatters,
): string | null {
  const distribution = insight.distribution;
  if (!distribution) return null;
  const { intl } = formatters;
  if (distribution.kind === "numeric") {
    const number = (value: number) => numericActual(insight.condition, value, intl);
    return [
      intl.formatMessage({ id: "match.result.insights.medianShort" }, { value: number(distribution.median) }),
      intl.formatMessage({ id: "match.result.insights.averageShort" }, { value: number(distribution.average) }),
      intl.formatMessage(
        { id: "match.result.insights.sdShort" },
        { value: number(distribution.standardDeviation) },
      ),
    ].join(" · ");
  }
  return intl.formatMessage(
    { id: "match.result.insights.textDistribution" },
    {
      value: formatters.actual(distribution.condition, distribution.topValue) ?? distribution.topValue,
      count: distribution.topCount,
      present: distribution.present,
      distinct: distribution.distinctCount,
    },
  );
}

/**
 * Einen numerischen Verteilungswert so schreiben, wie der Bildschirm ihn schreibt
 * (formatAnalysisActual in RankingInsightsPanel): Fachwissen als Stufe auf der 7er-Skala statt
 * als genesis-Speicherprozent, Erfahrung mit ihrer Einheit. Ohne das stuende "Median 71,4" neben
 * einem "Nachweis: Specialist" fuer dasselbe Kriterium.
 */
function numericActual(condition: unknown, value: number, intl: IntlShape): string {
  const typename = conditionTypename(condition);
  if (typename === "IndustryExperienceCondition" || typename === "ExperienceCondition") {
    return intl.formatMessage(
      { id: "match.result.ranking.yearsValue" },
      { years: Math.round(value * 10) / 10 },
    );
  }
  if (typename === "ExpertiseCondition") {
    return intl.formatNumber((value * 7) / 100, { maximumFractionDigits: 1 });
  }
  return intl.formatNumber(value, { maximumFractionDigits: 1 });
}

/**
 * Die Empfehlung als {id, values} statt als fertiger Satz: der Renderer hat einen IntlProvider,
 * nur das Kriteriums-Label muss vorformatiert reisen.
 */
function recommendationRef(
  insights: ReturnType<typeof buildRankingInsights>,
  formatters: RankPdfFormatters,
): MatchResultPdfRankMessageRef | null {
  const recommendation = insights.recommendation;
  if (!recommendation) return { id: "match.result.insights.recommendNone", values: {} };
  switch (recommendation.type) {
    case "must":
      return {
        id: "match.result.insights.recommendMust",
        values: {
          criterion: formatters.label(recommendation.criterion.condition),
          failed: recommendation.criterion.failedCount,
          total: recommendation.criterion.total,
        },
      };
    case "data":
      return {
        id: "match.result.insights.recommendData",
        values: {
          criterion: formatters.label(recommendation.criterion.condition),
          missing: recommendation.criterion.missingActualCount,
          total: recommendation.criterion.expectedActualCount,
        },
      };
    case "ai":
      return {
        id: "match.result.insights.recommendAi",
        values: {
          count: recommendation.reasonCount,
          candidates: recommendation.candidateCount,
        },
      };
    case "bottleneck":
      return {
        id: "match.result.insights.recommendBottleneck",
        values: {
          criterion: formatters.label(recommendation.criterion.condition),
          average: formatters.intl.formatNumber(recommendation.criterion.averageFulfillment, {
            style: "percent",
            maximumFractionDigits: 0,
          }),
        },
      };
  }
}

function criterionState(
  criterion: RankedCriterion,
  leniency: RequirementLeniencyView,
): MatchResultPdfRankCriterionState["state"] {
  const met = (criterion.fulfillment ?? 0) >= 1;
  if (!criterion.assessable) return "unknown";
  if (!met) return "unmet";
  return isLenientLevelMatch(criterion.condition, criterion.requirement, criterion.aiReason, met, leniency)
    ? "lenient"
    : "met";
}

function candidateDetail(
  candidate: RankedCandidate,
  member: FactSkillMemberView | undefined,
  leniency: RequirementLeniencyView,
  formatters: RankPdfFormatters,
) {
  const assessment = member?.aiAssessment ?? null;
  const suitability = percent(candidate.suitabilityScore);
  const criteria: MatchResultPdfRankCriterionState[] = candidate.criteria
    .map((criterion) => ({
      label: labelWithConstraint(criterion.condition, formatters),
      isMust: criterion.requirement === "MUST",
      state: criterionState(criterion, leniency),
      actual: formatters.actual(criterion.condition, criterion.actual),
      aiReason: criterion.aiReason?.trim() ? criterion.aiReason.trim() : null,
      order: formatters.kindOrder(criterion.condition),
    }))
    .sort((left, right) => left.order - right.order)
    .map(({ order: _order, ...rest }) => rest);
  return {
    suitability,
    suitabilityRatingId: suitability == null ? null : suitabilityRatingId(suitability),
    positive: assessmentItems(assessment?.positive),
    negative: assessmentItems(assessment?.negative),
    fazit: assessment?.fazit?.trim() ? assessment.fazit.trim() : null,
    questions: (assessment?.interviewQuestions ?? [])
      .map((question) => question.trim())
      .filter(Boolean)
      .slice(0, 3),
    musts: candidate.criteria
      .filter((criterion) => criterion.requirement === "MUST")
      .map((criterion) => ({
        label: labelWithConstraint(criterion.condition, formatters),
        state: criterionState(criterion, leniency),
      })),
    criteria,
  };
}

/**
 * Label plus Auflage, wo die Auflage etwas sagt. "all" / "any" / "min 1" / "exact 1" sind in
 * diesem Modus Formular-Defaults und werden - wie auf dem Schirm - unterdrueckt.
 */
function labelWithConstraint(condition: unknown, formatters: RankPdfFormatters): string {
  const label = formatters.label(condition);
  const raw = constraintSummary(condition);
  const constraint = raw === "all" || raw === "any" || raw === "min 1" || raw === "exact 1" ? "" : raw;
  return constraint ? `${label} (${constraint})` : label;
}

// Bedingungen ueber ihren serialisierten Inhalt zuordnen, nicht ueber die Objektidentitaet:
// jedes Mitglied traegt sein eigenes JSON.parse-Ergebnis desselben Kriteriums, Referenzgleichheit
// trifft also nur den ersten Kandidaten. Dieselbe Zuordnung nutzt buildRankingInsights.
function conditionSignature(condition: unknown): string {
  return condition === undefined ? "undefined" : JSON.stringify(condition);
}

const signatureCache = new WeakMap<RankedCandidate, Map<string, RankedCriterion>>();

function criteriaBySignature(candidate: RankedCandidate): Map<string, RankedCriterion> {
  const cached = signatureCache.get(candidate);
  if (cached) return cached;
  const map = new Map<string, RankedCriterion>();
  for (const criterion of candidate.criteria) {
    const key = conditionSignature(criterion.condition);
    if (!map.has(key)) map.set(key, criterion);
  }
  signatureCache.set(candidate, map);
  return map;
}

/**
 * Wer im Dokument steht. Ohne Wahl des Nutzers: die Baender A und B - die Linie, die er mit dem
 * Schwellenregler selbst gezogen hat. Waere die leer (ein Pool ganz ohne A und B), steht der
 * ganze Pool drin: ein Bericht ohne seinen Gegenstand ist keiner.
 */
function resolveSelection(
  ranked: ReadonlyArray<{ id: string; band: string }>,
  selected: ReadonlySet<string> | null,
): ReadonlySet<string> {
  if (selected) return selected;
  const shortlist = ranked.filter((entry) => entry.band === "A" || entry.band === "B");
  return new Set((shortlist.length > 0 ? shortlist : ranked).map((entry) => entry.id));
}

export function buildMatchResultPdfRank(input: RankPdfInput): MatchResultPdfRank | null {
  const { view, persons, thresholds, requirementLeniency, formatters } = input;
  const teamFit = resolveTeamFit(view, input.sortBy, input.teamFitBasis);
  const sortBy = teamFit.sortBy;
  const incumbentIds = teamFit.incumbentIds;
  const factSkill = teamFit.withoutIncumbents(view.factSkill);
  if (!factSkill || factSkill.teamMembers.length === 0) return null;

  const powerFitById = buildPowerFit(view.powers, input.elementWeights ?? undefined);
  const teamFitById = teamFit.contributionById;
  const ranked = buildCandidateRanking(factSkill, persons, thresholds, powerFitById, sortBy, teamFitById)
    .filter(c => !incumbentIds.has(c.personId));
  if (ranked.length === 0) return null;
  const insights = buildRankingInsights(factSkill, ranked);
  const memberById = new Map(factSkill.teamMembers.map((member) => [member.personId, member]));
  const personById = new Map(persons.map((person) => [person.id, person]));

  const includedIds = resolveSelection(
    ranked.map((candidate) => ({ id: candidate.personId, band: candidate.cluster })),
    input.selectedPersonIds,
  );
  const candidates: MatchResultPdfRankCandidate[] = ranked.map((candidate) => {
    const included = includedIds.has(candidate.personId);
    return {
      personId: candidate.personId,
      name: candidate.personName,
      subtitle: personById.get(candidate.personId)?.profession ?? null,
      band: candidate.cluster,
      smyclesScore: percent(candidate.suitabilityScore),
      requirementFit: candidate.kannDefined === 0 ? null : percent(candidate.score),
      powerFit: candidate.powerFit == null ? null : Math.round(candidate.powerFit),
      ...(teamFit ? { teamFit: candidate.teamFit ?? null } : {}),
      mustMet: candidate.mustMet,
      mustTotal: candidate.mustTotal,
      kannSum: candidate.kannSum,
      kannTotal: candidate.kannTotal,
      kannDefined: candidate.kannDefined,
      reviewReasonId: reviewReasonId(candidate.cluster, candidate.qualification, candidate.score),
      included,
      // Der schwere Detailblock entsteht nur fuer die, die auch im Dokument stehen.
      detail: included && input.withDetails
        ? candidateDetail(candidate, memberById.get(candidate.personId), requirementLeniency, formatters)
        : null,
    };
  });

  const radiusInsight = buildRadiusInsight(ranked);
  const radius = radiusInsight
    ? {
        city: radiusInsight.city,
        radiusKm: radiusInsight.radiusKm,
        inside: radiusInsight.points.filter((point) => point.inside).length,
        outside: radiusInsight.points.filter((point) => !point.inside).length,
        missing: radiusInsight.missing.length,
      }
    : null;

  // Die Anforderungsliste kommt vom ersten Kandidaten: jedes Mitglied traegt dieselben Kriterien
  // in derselben Reihenfolge, und genau darauf baut auch buildRankingInsights auf.
  const referenceCriteria = ranked[0]?.criteria ?? [];
  // Aus insights.criteria, nicht aus referenceCriteria: dieselben Kriterien, aber mit den
  // Pool-Zahlen daran. criterionRow setzt das nackte Label, labelWithConstraint haengt die
  // Auflage an ("Deutsch (mindestens C1)") - sonst stuende im Bericht ein anderes Label als
  // im Kandidaten-Detail darunter.
  const requirements = insights.criteria
    .map((insight) => ({
      ...criterionRow(insight, formatters, false),
      label: labelWithConstraint(insight.condition, formatters),
      order: formatters.kindOrder(insight.condition),
    }))
    .sort((left, right) => left.order - right.order)
    .map(({ order: _order, ...rest }) => rest);

  // Die Matrix ist vollstaendig - Muss und Kann. Nur die Muss-Spalten zu zeigen beantwortet
  // genau die Frage, die die Muss-Spalte der Rangliste schon beantwortet; der Wert einer Matrix
  // liegt im Rest.
  const matrixSource = referenceCriteria;
  const matrix: MatchResultPdfRequirementMatrix | null =
    input.withMatrix && matrixSource.length > 0
      ? {
          columns: matrixSource.map((criterion, position) => ({
            label: `R${position + 1}`,
            fullLabel: labelWithConstraint(criterion.condition, formatters),
            isMust: criterion.requirement === "MUST",
          })),
          rows: ranked.filter((candidate) => includedIds.has(candidate.personId)).map((candidate) => ({
            personId: candidate.personId,
            personName: candidate.personName,
            cells: matrixSource.map((column) => {
              const own = criteriaBySignature(candidate).get(conditionSignature(column.condition));
              return (own?.fulfillment ?? 0) >= 1;
            }),
          })),
        }
      : null;

  const counts = insights.pool.counts;
  return {
    pool: {
      total: insights.pool.total,
      review: counts.REVIEW,
      unqualified: counts.UNQUALIFIED,
      bands: { A: counts.A, B: counts.B, C: counts.C },
    },
    thresholds: { a: thresholds.a, b: thresholds.b, c: thresholds.c ?? 0 },
    sortBy,
    ...(view.recruitingTeamFit ? { teamFitBasis: view.recruitingTeamFit.basis } : {}),
    recommendation: recommendationRef(insights, formatters),
    hardestMust: insights.hardestMust
      ? {
          label: formatters.label(insights.hardestMust.condition),
          failed: insights.hardestMust.count,
          total: insights.hardestMust.total,
          passPercent: Math.round(
            ((insights.hardestMust.total - insights.hardestMust.count) /
              Math.max(1, insights.hardestMust.total)) *
              100,
          ),
        }
      : null,
    topFulfilled: insights.topFulfilled
      .slice(0, RANK_INSIGHT_ROW_CAP)
      .map((insight) => criterionRow(insight, formatters, false)),
    bottlenecks: insights.bottlenecks
      .slice(0, RANK_INSIGHT_ROW_CAP)
      .map((insight) => criterionRow(insight, formatters, true)),
    ai: { reasonCount: insights.ai.reasonCount },
    dataQuality: {
      missing: insights.dataQuality.missingActualCount,
      expected: insights.dataQuality.expectedActualCount,
    },
    radius,
    requirements,
    candidates,
    matrix,
  };
}

// ─── Unternehmen: derselbe Bericht, dieselben Bausteine ──────────────────────
//
// Ein Unternehmens-Ranking ist dieselbe Frage an einen anderen Gegenstand, also traegt es
// dieselbe Nutzlast. Was Unternehmen nicht haben - Powers, ganzheitliche KI-Eignung, Umkreis -
// bleibt null; der Renderer laesst die Spalte dann weg, statt eine leere zu zeichnen. Die
// Zahlen kommen aus buildCompanyRankingInsights, also aus derselben Quelle wie der Schirm.

/**
 * Was die Unternehmensseite an Beschriftung mitbringt. Eigene Signatur, weil ein
 * Unternehmens-Ist-Wert am Kriterium UND an der Zeile haengt: der Lauf hat den Wert erfasst,
 * die Zeile traegt nur den Rueckfall fuer Alt-Blobs (CompanyCriterionView.actualRecorded).
 */
export interface CompanyRankPdfFormatters {
  label: (condition: unknown) => string;
  actual: (criterion: CompanyCriterionView, row: RankedCompany) => string;
  kindOrder: (condition: unknown) => number;
  intl: IntlShape;
}

export interface CompanyRankPdfInput {
  ranked: RankedCompany[];
  thresholds: RankThresholds;
  withDetails: boolean;
  withMatrix: boolean;
  /** Wer im Dokument steht; null = noch keine Wahl, dann greifen die Baender A und B. */
  selectedEntryIds: ReadonlySet<string> | null;
  formatters: CompanyRankPdfFormatters;
}

function companyCriterionState(
  criterion: CompanyCriterionView,
): MatchResultPdfRankCriterionState["state"] {
  // Kein "lenient": die Stufen-Nachsicht ist eine Eigenschaft der Personen-Kriterien
  // (isLenientLevelMatch liest requirement und aiReason einer RankedCriterion).
  if (!criterion.assessable) return "unknown";
  return (criterion.fulfillment ?? 0) >= 1 ? "met" : "unmet";
}

export function buildMatchResultPdfRankForCompanies(
  input: CompanyRankPdfInput,
): MatchResultPdfRank | null {
  const { ranked, thresholds, formatters } = input;
  if (ranked.length === 0) return null;
  const insights = buildCompanyRankingInsights(ranked, thresholds);
  // criterionRow und recommendationRef sprechen die Personen-Signatur. Nur `actual` weicht ab,
  // und die Verteilungszeile fasst sie auf dieser Seite nie an: company-ranking-derive liefert
  // durchgehend distribution: null.
  const shared: RankPdfFormatters = {
    label: formatters.label,
    actual: (_condition, actual) => actual,
    kindOrder: formatters.kindOrder,
    intl: formatters.intl,
  };

  const bandOf = new Map(ranked.map((row) => [row.entryId, companyBand(row, thresholds)]));
  const includedIds = resolveSelection(
    ranked.map((row) => ({ id: row.entryId, band: bandOf.get(row.entryId)! })),
    input.selectedEntryIds,
  );

  const candidates: MatchResultPdfRankCandidate[] = ranked.map((row) => {
    const included = includedIds.has(row.entryId);
    const band = bandOf.get(row.entryId)!;
    const criteria = [...row.criteria]
      .map((criterion) => ({
        criterion,
        order: formatters.kindOrder(criterion.condition),
      }))
      .sort((left, right) => left.order - right.order)
      .map(({ criterion }) => criterion);
    return {
      personId: row.entryId,
      name: row.name,
      // Der Inaktiv-Hinweis steht, wo bei Personen der Beruf steht: dieselbe Zeile unter dem
      // Namen, und die einzige Angabe, die eine Unternehmenszeile dort zu machen hat.
      subtitle: row.inactive
        ? formatters.intl.formatMessage({ id: "match.pdf.inactive", defaultMessage: "inactive" })
        : null,
      band,
      // Keine ganzheitliche KI-Eignung auf dieser Seite - die Spalte faellt weg.
      smyclesScore: null,
      requirementFit: row.optionalTotal === 0 || row.score == null ? null : Math.round(row.score * 100),
      powerFit: null,
      mustMet: row.mustMet,
      mustTotal: row.mustTotal,
      // score ist das MITTEL der bewertbaren Kann-Kriterien; der Chip zeigt die Summe, damit
      // kannSum/kannTotal wieder den Score ergibt - wie auf der Personenseite.
      kannSum: row.score == null ? 0 : row.score * row.optionalAssessed,
      kannTotal: row.optionalAssessed,
      kannDefined: row.optionalTotal,
      reviewReasonId: reviewReasonId(band, row.qualification, row.score),
      included,
      detail:
        included && input.withDetails
          ? {
              suitability: null,
              suitabilityRatingId: null,
              positive: [],
              negative: [],
              fazit: null,
              questions: [],
              musts: criteria
                .filter((criterion) => criterion.isMust)
                .map((criterion) => ({
                  label: formatters.label(criterion.condition),
                  state: companyCriterionState(criterion),
                })),
              criteria: criteria.map((criterion) => ({
                label: formatters.label(criterion.condition),
                isMust: criterion.isMust,
                state: companyCriterionState(criterion),
                actual: formatters.actual(criterion, row),
                aiReason: criterion.aiReason?.trim() ? criterion.aiReason.trim() : null,
              })),
            }
          : null,
    };
  });

  const requirements = insights.criteria
    .map((insight) => ({
      ...criterionRow(insight, shared, false),
      order: formatters.kindOrder(insight.condition),
    }))
    .sort((left, right) => left.order - right.order)
    .map(({ order: _order, ...rest }) => rest);

  // Die Matrix nimmt die Kriterien der ersten Zeile als Spalten - jede Zeile traegt dieselben
  // in derselben Reihenfolge, genau wie auf der Personenseite.
  const matrixSource = ranked[0]?.criteria ?? [];
  const matrix: MatchResultPdfRequirementMatrix | null =
    input.withMatrix && matrixSource.length > 0
      ? {
          columns: matrixSource.map((criterion, position) => ({
            label: `R${position + 1}`,
            fullLabel: formatters.label(criterion.condition),
            isMust: criterion.isMust,
          })),
          rows: ranked
            .filter((row) => includedIds.has(row.entryId))
            .map((row) => ({
              personId: row.entryId,
              personName: row.name,
              cells: matrixSource.map((_column, position) => {
                const own = row.criteria[position];
                return (own?.fulfillment ?? 0) >= 1;
              }),
            })),
        }
      : null;

  const counts = insights.pool.counts;
  return {
    pool: {
      total: insights.pool.total,
      review: counts.REVIEW,
      unqualified: counts.UNQUALIFIED,
      bands: { A: counts.A, B: counts.B, C: counts.C },
    },
    thresholds: { a: thresholds.a, b: thresholds.b, c: thresholds.c ?? 0 },
    // Die Unternehmensliste kennt nur eine Achse: den Anforderungs-Fit. Der Schirm bietet
    // keinen Sortierwahlschalter an, also behauptet das Papier auch keinen.
    sortBy: "requirements",
    recommendation: recommendationRef(insights, shared),
    hardestMust: insights.hardestMust
      ? {
          label: formatters.label(insights.hardestMust.condition),
          failed: insights.hardestMust.count,
          total: insights.hardestMust.total,
          passPercent: Math.round(
            ((insights.hardestMust.total - insights.hardestMust.count) /
              Math.max(1, insights.hardestMust.total)) *
              100,
          ),
        }
      : null,
    topFulfilled: insights.topFulfilled
      .slice(0, RANK_INSIGHT_ROW_CAP)
      .map((insight) => criterionRow(insight, shared, false)),
    bottlenecks: insights.bottlenecks
      .slice(0, RANK_INSIGHT_ROW_CAP)
      .map((insight) => criterionRow(insight, shared, true)),
    ai: { reasonCount: insights.ai.reasonCount },
    dataQuality: {
      missing: insights.dataQuality.missingActualCount,
      expected: insights.dataQuality.expectedActualCount,
    },
    // Kein Umkreis-Kriterium auf dieser Seite.
    radius: null,
    requirements,
    candidates,
    matrix,
  };
}
